import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { ProgressBar } from '../ui/ProgressBar';
import { StatusIndicator } from '../ui/StatusIndicator';
import { Button } from '../ui/Button';
import { Download, Play, X } from 'lucide-react';
import { JobStatus } from '../../hooks/useJobStatus';

export interface GenerationProgressProps {
  jobStatus: JobStatus | null;
  onDownload?: () => void;
  onReset?: () => void;
  downloadError?: string | null;
}

/** Controls how quickly the simulated progress bar fills during the rendering phase.
 * Applied as a fraction of the remaining gap each tick: step = gap * RATE.
 * Lower values → slower / more gradual approach to the 89 % ceiling. */
const PROGRESS_ACCELERATION_RATE = 0.012;

/**
 * Maps backend status+progress to a display-friendly progress value with smooth
 * interpolation during the rendering phase so the bar never feels stuck.
 *
 * Stage ranges:
 *   queued / uploading  →  0–10 %
 *   preprocessing       → 10–30 %
 *   rendering           → 30–90 %  (simulated smooth fill)
 *   completed           → 100 %
 *   failed              → current value (no animation)
 */
function useSmoothedProgress(jobStatus: JobStatus | null): number {
  const [displayProgress, setDisplayProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!jobStatus) return;

    const status = jobStatus.status;
    const raw = jobStatus.progress ?? 0;

    // Clear any running simulation
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (status === 'completed') {
      setDisplayProgress(100);
      return;
    }

    if (status === 'failed') {
      setDisplayProgress(raw);
      return;
    }

    // Map backend progress to stage-bounded display progress
    let target: number;
    if (status === 'queued' || status === 'uploading') {
      target = Math.min(10, raw);
    } else if (status === 'preprocessing') {
      target = 10 + Math.min(20, raw * 0.2);
    } else if (status === 'rendering') {
      // Backend may report 0–100; map to 30–90 display range.
      // If the backend value is already in a reasonable range use it,
      // otherwise floor at 30.
      const backendMapped = 30 + (raw / 100) * 60;
      target = Math.max(30, Math.min(90, backendMapped));
    } else {
      target = raw;
    }

    // Snap immediately to the target if we are behind
    setDisplayProgress((prev) => {
      if (prev > target) return prev; // never go backwards
      return target;
    });

    // During rendering: simulate gradual fill toward 89 % (leave 90 for "finalizing")
    if (status === 'rendering') {
      timerRef.current = setInterval(() => {
        setDisplayProgress((prev) => {
          if (prev >= 89) {
            if (timerRef.current !== null) clearInterval(timerRef.current);
            return 89;
          }
          // Slow logarithmic approach: accelerate early, decelerate late
          const step = Math.max(0.1, (89 - prev) * PROGRESS_ACCELERATION_RATE);
          return Math.min(89, prev + step);
        });
      }, 800);
    }

    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobStatus, jobStatus?.status, jobStatus?.progress]);

  return displayProgress;
}

export const GenerationProgress: React.FC<GenerationProgressProps> = ({
  jobStatus,
  onDownload,
  onReset,
  downloadError,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const smoothedProgress = useSmoothedProgress(jobStatus);

  if (!jobStatus) return null;

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      queued: 'Queued',
      preprocessing: 'Preprocessing Assets',
      rendering: 'Rendering Video',
      completed: 'Completed',
      failed: 'Failed',
    };
    return labels[status] || status;
  };

  const getProgressVariant = (): 'default' | 'success' | 'warning' | 'error' => {
    if (jobStatus.status === 'failed') return 'error';
    if (jobStatus.status === 'completed') return 'success';
    return 'default';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Video Generation</CardTitle>
          <StatusIndicator
            status={jobStatus.status === 'uploading' ? 'rendering' : jobStatus.status}
            label={getStatusLabel(jobStatus.status)}
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <ProgressBar
            progress={smoothedProgress}
            variant={getProgressVariant()}
            showPercentage={true}
          />

          {jobStatus.status === 'completed' && jobStatus.outputVideoPublicUrl && (
            <div className="p-4 bg-status-online/10 border border-status-online/20 rounded-md">
              <p className="text-sm text-status-online font-medium mb-3">
                Your video is ready!
              </p>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onDownload}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Video
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowPreview((v) => !v)}
                  className="flex items-center gap-2"
                >
                  {showPreview ? (
                    <>
                      <X className="w-4 h-4" />
                      Close Preview
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Preview
                    </>
                  )}
                </Button>
              </div>
              {downloadError && (
                <p className="text-xs text-accent-red mt-3">{downloadError}</p>
              )}
              {showPreview && jobStatus.outputVideoPublicUrl && (
                <div className="mt-3">
                  <video
                    src={jobStatus.outputVideoPublicUrl}
                    controls
                    className="w-full rounded-md border border-border-subtle"
                  />
                </div>
              )}
            </div>
          )}

          {jobStatus.status === 'failed' && (
            <div className="p-4 bg-accent-red/10 border border-accent-red/20 rounded-md">
              <p className="text-sm text-accent-red font-medium mb-2">
                Generation Failed
              </p>
              {jobStatus.error && (
                <p className="text-xs text-text-secondary mb-3">
                  {typeof jobStatus.error === 'string'
                    ? jobStatus.error
                    : jobStatus.error.message}
                </p>
              )}
              <Button variant="secondary" size="sm" onClick={onReset}>
                Try Again
              </Button>
            </div>
          )}

          {(jobStatus.status === 'queued' ||
            jobStatus.status === 'preprocessing' ||
            jobStatus.status === 'rendering') && (
            <div className="p-4 bg-bg-tertiary border border-border-subtle rounded-md">
              <p className="text-sm text-text-secondary">
                {jobStatus.status === 'queued' &&
                  'Your job is queued and will start processing soon...'}
                {jobStatus.status === 'preprocessing' &&
                  'Preparing your assets for rendering...'}
                {jobStatus.status === 'rendering' &&
                  'Rendering your video with Remotion Lambda...'}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
